<script setup>
import { computed } from 'vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';

// `hasActiveFilters` distingue dois cenários de lista vazia:
//  - sem filtros: realmente não há eventos ainda na conta;
//  - com filtros: existem eventos, mas nenhum casa com o período/filtros atuais.
// As props `title`/`subtitle` continuam permitindo override explícito quando
// passadas; caso contrário, usamos o texto adequado ao cenário.
const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  hasActiveFilters: { type: Boolean, default: false },
});

const resolvedTitle = computed(
  () =>
    props.title ||
    (props.hasActiveFilters
      ? 'Nenhum evento no período'
      : 'Nenhum evento ainda')
);

const resolvedSubtitle = computed(
  () =>
    props.subtitle ||
    (props.hasActiveFilters
      ? 'Nenhum evento casa com o período e os filtros selecionados. Amplie o período ou limpe os filtros para ver mais resultados.'
      : 'Eventos auditáveis aparecem aqui conforme ocorrem na plataforma - login, edições em inboxes, automações, macros, regras e mais.')
);

const SAMPLES = [
  {
    icon: 'i-lucide-pencil',
    iconBg: 'bg-n-teal-3',
    iconColor: 'text-n-teal-11',
    actorLabel: 'Update',
    title: 'Maria Souza editou inbox "Atendimento WhatsApp"',
    target: 'Inbox · #atendimento-wpp',
    when: 'há 2 min',
  },
  {
    icon: 'i-lucide-plus-circle',
    iconBg: 'bg-n-grass-3',
    iconColor: 'text-n-grass-11',
    actorLabel: 'Create',
    title: 'João Lima criou automação "Roteamento por intenção"',
    target: 'AutomationRule · #routing-intent',
    when: 'há 18 min',
  },
  {
    icon: 'i-lucide-trash-2',
    iconBg: 'bg-n-ruby-3',
    iconColor: 'text-n-ruby-11',
    actorLabel: 'Destroy',
    title: 'Rafael Costa removeu macro "Fechar e arquivar"',
    target: 'Macro · #close-archive',
    when: 'há 1 h',
  },
];
</script>

<template>
  <EmptyStateLayout :title="resolvedTitle" :subtitle="resolvedSubtitle">
    <template #empty-state-item>
      <div class="flex flex-col gap-3 p-px">
        <div
          v-for="(ev, i) in SAMPLES"
          :key="i"
          class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 flex items-center gap-4"
        >
          <span
            class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
            :class="ev.iconBg"
          >
            <i :class="[ev.icon, ev.iconColor]" class="size-5" />
          </span>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <p class="text-sm font-semibold text-n-slate-12 mb-0 truncate">{{ ev.title }}</p>
              <span class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-alpha-2 text-[10px] font-medium text-n-slate-11 uppercase tracking-wide">
                {{ ev.actorLabel }}
              </span>
            </div>
            <div class="flex items-center gap-3 mt-0.5 text-xs text-n-slate-10">
              <span class="inline-flex items-center gap-1">
                <i class="i-lucide-target size-3" />
                {{ ev.target }}
              </span>
              <span class="inline-flex items-center gap-1">
                <i class="i-lucide-clock size-3" />
                {{ ev.when }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <slot name="actions" />
    </template>
  </EmptyStateLayout>
</template>
